/**
 * Einheitliche FHIR-basierte Vitaldaten Typen
 * Zentrale Datenstruktur für alle Vitaldaten-Komponenten
 */

// FHIR R5 Observation Resource für Vitaldaten
export interface FHIRVitalObservation {
  resourceType: "Observation";
  id?: string;
  status:
    | "final"
    | "preliminary"
    | "registered"
    | "cancelled"
    | "entered-in-error";
  category?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
  }>;
  code: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
  };
  subject?: {
    reference?: string;
    display?: string;
  };
  effectiveDateTime?: string;
  issued?: string;
  valueQuantity?: {
    value?: number;
    unit?: string;
    system?: string;
    code?: string;
  };
  component?: Array<{
    code: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
    };
    valueQuantity?: {
      value?: number;
      unit?: string;
      system?: string;
      code?: string;
    };
  }>;
  meta?: {
    lastUpdated?: string;
    versionId?: string;
  };
}

// Einheitliche UI-Datenstruktur basierend auf FHIR
export interface VitalSignsData {
  // Blutdruck-Komponenten
  systolicBloodPressure?: number;
  diastolicBloodPressure?: number;
  bloodPressureUnit: "mmHg";

  // Herzfrequenz
  restingHeartRate?: number;
  heartRateUnit: "bpm";

  // ✅ NEW: Wellness-Komponenten (FHIR LOINC 72133-2, 72134-0)
  physicalWellness?: number; // Scale 1-5
  emotionalWellness?: number; // Scale 1-5

  // Zeitstempel
  recordedAt: string;

  // FHIR Metadaten
  fhirId?: string;
  fhirStatus: "final" | "preliminary";

  // Berechnete Werte (optional)
  pulsPressure?: number | undefined; // systolic - diastolic
  meanArterialPressure?: number | undefined; // diastolic + (pulse pressure / 3)
}

// Input-Datenstruktur für neue Messungen
export interface VitalSignsInput {
  systolicBloodPressure: number;
  diastolicBloodPressure: number;
  restingHeartRate: number;
}

// Verlaufsdaten für Diagramme
export interface VitalSignsHistoryItem {
  date: string; // ISO date string
  systolic: number;
  diastolic: number;
  heartRate: number;
  timestamp: string; // Full ISO timestamp
  fhirId?: string;
}

// API Response Struktur
export interface VitalSignsResponse {
  success: boolean;
  data: {
    message?: string;
    vitalSigns?: VitalSignsData;
    fhirObservation?: FHIRVitalObservation;
    history?: VitalSignsHistoryItem[];
    period?: string;
    totalRecords?: number;
    recordedAt?: string;
  };
  meta: {
    timestamp: string;
    version: string;
  };
}

// Validierungsstruktur
export interface VitalSignsValidation {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string>;
}

// Status-Kategorien für UI
export interface VitalSignsStatus {
  bloodPressure: {
    category:
      | "normal"
      | "elevated"
      | "high_stage1"
      | "high_stage2"
      | "hypertensive_crisis"
      | "low";
    label: string;
    color: string;
    bgColor: string;
  };
  heartRate: {
    category: "bradycardia" | "normal" | "tachycardia";
    label: string;
    color: string;
    bgColor: string;
  };
}

// Dashboard-Display Format
export interface VitalSignsDisplayData {
  systolic: number;
  diastolic: number;
  heartRate: number;
  recordedAt: string;
  status: VitalSignsStatus;
  trend?: "up" | "down" | "stable";
}

// Chart-Datenformat für REINE Vital Signs (Blutdruck + Herzfrequenz)
export interface VitalSignsChartData {
  date: string;
  systolic: number;
  diastolic: number;
  heartRate: number;
  timestamp: string;
  formattedDate: string;
  // ✅ REMOVED: physicalWellness, emotionalWellness - gehören in WellbeingChartData
}

// ✅ SEPARATE Chart-Datenformat für REINE Wellbeing-Daten (Smiley-Interface)
export interface WellbeingChartData {
  date: string;
  physicalWellbeing: number; // Scale 1-5 (LOINC 72133-2)
  emotionalWellbeing: number; // Scale 1-5 (LOINC 72134-0)
  timestamp: string;
  formattedDate: string;
}

// Legacy Interface - wird durch WellbeingChartData ersetzt
export interface WellnessDataPoint {
  date: string;
  physicalWellness: number; // Scale 1-5
  emotionalWellness: number; // Scale 1-5
  timestamp: string;
  formattedDate: string;
}

// Konstanten für FHIR LOINC Codes
export const FHIR_LOINC_CODES = {
  BLOOD_PRESSURE_PANEL: "85354-9",
  SYSTOLIC_BP: "8480-6",
  DIASTOLIC_BP: "8462-4",
  HEART_RATE: "8867-4",
  PHYSICAL_WELLNESS: "72133-2",
  EMOTIONAL_WELLNESS: "72134-0",
  VITAL_SIGNS_CATEGORY: "vital-signs",
} as const;

// Konstanten für Validierungsgrenzwerte
export const VITAL_SIGNS_LIMITS = {
  SYSTOLIC: { min: 70, max: 250 },
  DIASTOLIC: { min: 40, max: 150 },
  HEART_RATE: { min: 30, max: 220 },
} as const;

// Konstanten für Status-Klassifizierung
export const BP_CATEGORIES = {
  LOW: { systolic: { max: 90 }, diastolic: { max: 60 } },
  NORMAL: { systolic: { max: 120 }, diastolic: { max: 80 } },
  ELEVATED: { systolic: { max: 129 }, diastolic: { max: 80 } },
  HIGH_STAGE1: { systolic: { max: 139 }, diastolic: { max: 89 } },
  HIGH_STAGE2: { systolic: { max: 179 }, diastolic: { max: 109 } },
  HYPERTENSIVE_CRISIS: { systolic: { min: 180 }, diastolic: { min: 110 } },
} as const;

export const HR_CATEGORIES = {
  BRADYCARDIA: { max: 59 },
  NORMAL: { min: 60, max: 100 },
  TACHYCARDIA: { min: 101 },
} as const;
